import React, { useState, useMemo } from 'react';
import {
  Table,
  Tag,
  Button,
  Card,
  Statistic,
  Row,
  Col,
  notification,
  Input,
  Space,
  Progress,
  Typography,
  Checkbox,
  Modal,
  Alert,
  Radio,
  Tooltip,
} from 'antd';
import {
  ThunderboltOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  LoadingOutlined,
  AuditOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import * as api from 'util/api';
import './index.less';

const { Text } = Typography;

const COVERAGE_CONFIG = {
  full: { label: 'Full', color: 'green' },
  partial: { label: 'Partial', color: 'orange' },
  none: { label: 'None', color: 'default' },
};

const ISSUE_CONFIG = {
  sku_barcode_wrong_format: {
    label: 'Wrong format on SKU',
    color: 'red',
    icon: <CloseCircleOutlined />,
    rowClass: 'issue-row-wrong-format-sku',
    description: 'The SKU barcode belongs to a different pack/format than this legacy field. Master sheet confirms legacy is correct.',
  },
  legacy_barcode_wrong_format: {
    label: 'Wrong format on legacy',
    color: 'orange',
    icon: <ExclamationCircleOutlined />,
    rowClass: 'issue-row-wrong-format-legacy',
    description: 'The legacy barcode belongs to a different pack/format; the SKU barcode matches the expected format.',
  },
  wrong_sku_case_type: {
    label: 'Wrong SKU case type',
    color: 'gold',
    icon: <QuestionCircleOutlined />,
    rowClass: 'issue-row-wrong-case',
    description: 'A SKU exists for this size but with the wrong case type (e.g. 24x1 instead of 15x1).',
  },
  mismatch: {
    label: 'Value differs',
    color: 'red',
    icon: <CloseCircleOutlined />,
    rowClass: 'issue-row-mismatch',
    description: 'Legacy and SKU barcodes differ; format fit is ambiguous — review manually.',
  },
  empty_on_sku: {
    label: 'Empty on SKU',
    color: 'orange',
    icon: <ExclamationCircleOutlined />,
    rowClass: 'issue-row-empty',
    description: 'The matching SKU exists but its barcode slot is empty.',
  },
  no_matching_sku: {
    label: 'No matching SKU',
    color: 'gold',
    icon: <QuestionCircleOutlined />,
    rowClass: 'issue-row-missing',
    description: 'No SKU row matches this legacy field format.',
  },
};

const ISSUE_FILTER_ORDER = [
  'sku_barcode_wrong_format',
  'legacy_barcode_wrong_format',
  'wrong_sku_case_type',
  'mismatch',
  'empty_on_sku',
  'no_matching_sku',
];

const RECOMMENDATION_CONFIG = {
  use_legacy: { color: 'green', label: 'Use legacy barcode' },
  use_sku: { color: 'green', label: 'Use SKU barcode' },
  fix_sku_row: { color: 'gold', label: 'Fix SKU row' },
  review: { color: 'default', label: 'Review manually' },
};

const isValueConflictIssue = (issue) => [
  'mismatch',
  'sku_barcode_wrong_format',
  'legacy_barcode_wrong_format',
  'wrong_sku_case_type',
].includes(issue);

const barcodeValueStyle = (highlight, isCanonical) => {
  if (!highlight) return { fontSize: 12 };
  if (isCanonical) {
    return { fontSize: 12, color: '#389e0d', background: '#f6ffed' };
  }
  return { fontSize: 12, color: '#cf1322', background: '#fff1f0' };
};

const renderMasterMatch = (master, { hasBarcode, fit }) => {
  if (!hasBarcode) return <Text type="secondary">—</Text>;
  if (!master) {
    return (
      <Space size={4}>
        <span className="master-match-dot master-match-dot--miss" />
        <Text type="secondary">Not in master</Text>
      </Space>
    );
  }
  if (fit === false) {
    const subtitle = [master.unit, master.description].filter(Boolean).join(' · ');
    return (
      <Tooltip title={master.description || master.name}>
        <div className="master-match-cell master-match-cell--wrong">
          <Space size={4}>
            <CloseCircleOutlined style={{ color: '#cf1322' }} />
            <Text strong ellipsis style={{ maxWidth: 180, color: '#cf1322' }}>
              {master.name}
            </Text>
          </Space>
          <Text type="danger" style={{ fontSize: 11, display: 'block' }}>
            Wrong format
          </Text>
          {subtitle ? (
            <Text
              type="secondary"
              ellipsis
              style={{ fontSize: 11, maxWidth: 220, display: 'block' }}
            >
              {subtitle}
            </Text>
          ) : null}
        </div>
      </Tooltip>
    );
  }

  const subtitle = [master.unit, master.description].filter(Boolean).join(' · ');
  return (
    <Tooltip title={master.description || master.name}>
      <div className="master-match-cell master-match-cell--hit">
        <Space size={4}>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          <Text strong ellipsis style={{ maxWidth: 180 }}>
            {master.name}
          </Text>
        </Space>
        {subtitle ? (
          <Text
            type="secondary"
            ellipsis
            style={{ fontSize: 11, maxWidth: 220, display: 'block' }}
          >
            {subtitle}
          </Text>
        ) : null}
      </div>
    </Tooltip>
  );
};

const buildValidationIssueColumns = ({ onApplyFix, applyingFixKey } = {}) => [
  {
    title: 'Product',
    key: 'productName',
    width: 200,
    ellipsis: true,
    fixed: 'left',
    sorter: (a, b) => (a.productName || '').localeCompare(b.productName || ''),
    render: (_, row) => (
      <a
        href={`/dashboard/product-database/products/${row.productId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {row.productName || `Product ${row.productId}`}
      </a>
    ),
  },
  {
    title: 'Issue',
    dataIndex: 'issue',
    width: 180,
    sorter: (a, b) => (a.issue || '').localeCompare(b.issue || ''),
    render: (issue) => {
      const config = ISSUE_CONFIG[issue];
      if (!config) return <Tag>{issue}</Tag>;
      return (
        <Tooltip title={config.description}>
          <Tag color={config.color} icon={config.icon} style={{ margin: 0 }}>
            {config.label}
          </Tag>
        </Tooltip>
      );
    },
  },
  {
    title: 'Expected format',
    key: 'expectedFormat',
    width: 160,
    render: (_, row) => (
      row.expectedFormat?.label
        ? <Text code style={{ fontSize: 12 }}>{row.expectedFormat.label}</Text>
        : <Text type="secondary">—</Text>
    ),
  },
  {
    title: 'Recommendation',
    key: 'recommendation',
    width: 170,
    render: (_, row) => {
      const cfg = RECOMMENDATION_CONFIG[row.recommendation] || RECOMMENDATION_CONFIG.review;
      const label = row.recommendationLabel || cfg.label;
      return (
        <Tooltip title={row.diagnosis || label}>
          <Tag color={cfg.color} style={{ margin: 0, whiteSpace: 'normal' }}>
            {label}
          </Tag>
        </Tooltip>
      );
    },
  },
  {
    title: 'Legacy field',
    dataIndex: 'legacyField',
    width: 170,
    render: (v) => (v ? <Text code style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text>),
  },
  {
    title: 'Legacy value',
    dataIndex: 'legacyValue',
    width: 150,
    render: (v, row) => {
      if (!v) return <Text type="secondary">—</Text>;
      const highlight = isValueConflictIssue(row.issue);
      const isCanonical = row.recommendation === 'use_legacy';
      return (
        <Text
          code
          strong={highlight}
          style={barcodeValueStyle(highlight, isCanonical)}
        >
          {v}
        </Text>
      );
    },
  },
  {
    title: 'Legacy → master',
    key: 'legacyMaster',
    width: 240,
    render: (_, row) => renderMasterMatch(row.legacyMaster, {
      hasBarcode: Boolean(row.legacyValue),
      fit: row.legacyMasterFit,
    }),
  },
  {
    title: 'SKU',
    dataIndex: 'skuKey',
    width: 90,
    align: 'center',
    render: (v) => (v ? <Tag color="blue" style={{ margin: 0 }}>{`#${v}`}</Tag> : <Text type="secondary">—</Text>),
  },
  {
    title: 'Slot',
    dataIndex: 'barcodeSlot',
    width: 100,
    render: (v) => (v ? <Text code style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text>),
  },
  {
    title: 'SKU value',
    dataIndex: 'skuValue',
    width: 150,
    render: (v, row) => {
      if (!v) {
        if (row.issue === 'empty_on_sku') {
          return <Text type="warning" italic>(empty)</Text>;
        }
        return <Text type="secondary">—</Text>;
      }
      const highlight = isValueConflictIssue(row.issue);
      const isCanonical = row.recommendation === 'use_sku';
      return (
        <Text
          code
          strong={highlight}
          style={barcodeValueStyle(highlight, isCanonical)}
        >
          {v}
        </Text>
      );
    },
  },
  {
    title: 'SKU → master',
    key: 'skuMaster',
    width: 240,
    render: (_, row) => renderMasterMatch(row.skuMaster, {
      hasBarcode: Boolean(row.skuValue),
      fit: row.skuMasterFit,
    }),
  },
  {
    title: 'Fix',
    key: 'fix',
    width: 150,
    fixed: 'right',
    render: (_, row) => {
      const { remediation } = row;
      if (!remediation) return <Text type="secondary">—</Text>;

      if (remediation.reason === 'no_action') {
        return (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {remediation.statusLabel || 'No action needed'}
          </Text>
        );
      }

      if (!remediation.applicable) {
        return (
          <Tooltip title={remediation.message || remediation.statusLabel}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {remediation.statusLabel || 'Manual review'}
            </Text>
          </Tooltip>
        );
      }

      const fixKey = `${row.productId}-${row.legacyField}`;
      return (
        <Button
          type="primary"
          size="small"
          ghost
          icon={<ToolOutlined />}
          loading={applyingFixKey === fixKey}
          disabled={Boolean(applyingFixKey && applyingFixKey !== fixKey)}
          onClick={() => onApplyFix?.(row)}
        >
          Apply fix
        </Button>
      );
    },
  },
];

const STATUS_CONFIG = {
  ready: {
    label: 'Ready',
    color: 'gold',
    icon: <ClockCircleOutlined />,
    className: 'status-ready',
  },
  partial: {
    label: 'Partial',
    color: 'orange',
    icon: <ExclamationCircleOutlined />,
    className: 'status-partial',
  },
  complete: {
    label: 'Complete',
    color: 'green',
    icon: <CheckCircleOutlined />,
    className: 'status-complete',
  },
  no_legacy_data: {
    label: 'No Data',
    color: 'default',
    icon: <MinusCircleOutlined />,
    className: 'status-no-data',
  },
};

/**
 * SkuMigrationTable - Displays the SKU migration progress for all products.
 *
 * Shows which products have legacy barcode data that can be migrated to SKUs,
 * the current migration state, and provides per-product and bulk generation actions.
 */
const SkuMigrationTable = () => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [generatingProductId, setGeneratingProductId] = useState(null);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationReport, setValidationReport] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationScopeLabel, setValidationScopeLabel] = useState(null);
  const [validationIssueFilter, setValidationIssueFilter] = useState('all');
  const [validationScopeIds, setValidationScopeIds] = useState(null);
  const [fixConfirmRow, setFixConfirmRow] = useState(null);
  const [applyingFixKey, setApplyingFixKey] = useState(null);

  // Fetch migration status
  const { data: statuses, isLoading, refetch } = useQuery({
    queryKey: ['sku-migration-status'],
    queryFn: api.getSkuMigrationStatus,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  // Generate SKUs for a single product
  const generateMutation = useMutation({
    mutationFn: (productId) => api.generateSkusFromLegacy(productId),
    onSuccess: (results, productId) => {
      const createdCount = results.created?.length || 0;
      const failedCount = results.failed?.length || 0;
      if (createdCount > 0) {
        notification.success({
          message: 'SKUs Generated',
          description: `${createdCount} SKU${createdCount > 1 ? 's' : ''} created for product ${productId}.`,
        });
      }
      if (failedCount > 0) {
        notification.warning({
          message: 'Partial Success',
          description: `${failedCount} SKU${failedCount > 1 ? 's' : ''} failed.`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['sku-migration-status'] });
    },
    onError: (error, productId) => {
      notification.error({
        message: 'Generation Failed',
        description: `Failed to generate SKUs for product ${productId}.`,
      });
    },
  });

  const handleGenerate = async (productId) => {
    setGeneratingProductId(productId);
    try {
      await generateMutation.mutateAsync(productId);
    } finally {
      setGeneratingProductId(null);
    }
  };

  const generatableStatuses = useMemo(() => new Set(['ready', 'partial']), []);

  // Only products with status 'ready' or 'partial' are generatable
  const generatableSelectedKeys = useMemo(() => {
    if (!statuses) return [];
    return selectedRowKeys.filter((key) => {
      const item = statuses.find((s) => s.productId === key);
      return item && generatableStatuses.has(item.status);
    });
  }, [selectedRowKeys, statuses, generatableStatuses]);

  const productsNeedingGeneration = useMemo(() => {
    if (!statuses) return [];
    return statuses.filter((s) => generatableStatuses.has(s.status));
  }, [statuses, generatableStatuses]);

  const runValidationReport = async () => {
    setValidationLoading(true);
    setValidationIssueFilter('all');

    const scopedIds = selectedRowKeys.length > 0 ? selectedRowKeys : null;
    const scopeLabel = scopedIds
      ? `${scopedIds.length} selected product${scopedIds.length === 1 ? '' : 's'}`
      : 'all products';
    setValidationScopeLabel(scopeLabel);
    setValidationScopeIds(scopedIds);

    try {
      const params = { limit: 2000 };
      if (scopedIds) params.productIds = scopedIds.join(',');

      const data = await api.getSkuMigrationValidation(params);
      if (data == null) {
        notification.warning({
          message: 'No validation data returned',
          description: 'The request completed but the server returned no payload. Check the network response.',
        });
        return;
      }

      setValidationReport(data);
      setValidationOpen(true);
      if (data?.summary?.totalIssues === 0) {
        notification.success({
          message: 'Validation complete',
          description: `No barcode discrepancies found across ${scopeLabel}.`,
        });
      }
    } catch (err) {
      console.error('[Barcode validation] failed', err);
      notification.error({
        message: 'Validation failed',
        description: err?.message,
      });
    } finally {
      setValidationLoading(false);
    }
  };

  const refreshValidationReport = async () => {
    const params = { limit: 2000 };
    if (validationScopeIds?.length) {
      params.productIds = validationScopeIds.join(',');
    }
    const data = await api.getSkuMigrationValidation(params);
    if (data) {
      setValidationReport(data);
    }
    return data;
  };

  const handleApplyFix = (row) => {
    setFixConfirmRow(row);
  };

  const handleConfirmApplyFix = async () => {
    if (!fixConfirmRow) return;

    const fixKey = `${fixConfirmRow.productId}-${fixConfirmRow.legacyField}`;
    setApplyingFixKey(fixKey);

    try {
      const result = await api.applySkuBarcodeRemediation(
        fixConfirmRow.productId,
        fixConfirmRow.legacyField,
      );

      notification.success({
        message: 'Barcode fix applied',
        description: result?.summary || 'SKU barcode remediation completed.',
      });

      setFixConfirmRow(null);
      const refreshed = await refreshValidationReport();
      queryClient.invalidateQueries({ queryKey: ['sku-migration-status'] });

      if (refreshed?.summary?.totalIssues === 0) {
        notification.info({
          message: 'All issues resolved',
          description: 'No remaining barcode discrepancies in this validation scope.',
        });
      }
    } catch (err) {
      console.error('[Barcode remediation] failed', err);
    } finally {
      setApplyingFixKey(null);
    }
  };

  const handleBulkGenerateList = async (productList) => {
    if (!productList?.length) return;

    setBulkGenerating(true);
    setBulkProgress({ current: 0, total: productList.length });

    let successCount = 0;
    let failCount = 0;

    /* eslint-disable no-await-in-loop */
    for (let i = 0; i < productList.length; i += 1) {
      const product = productList[i];
      setBulkProgress({ current: i + 1, total: productList.length });

      try {
        const results = await api.generateSkusFromLegacy(product.productId);
        if (results.created?.length > 0) {
          successCount += 1;
        }
      } catch (error) {
        failCount += 1;
      }

      if (i < productList.length - 1) {
        await new Promise((resolve) => { setTimeout(resolve, 1000); });
      }
    }
    /* eslint-enable no-await-in-loop */

    notification.success({
      message: 'Bulk generation complete',
      description: `${successCount} product(s) created new SKUs where applicable.${failCount > 0 ? ` ${failCount} failed.` : ''}`,
    });

    setBulkGenerating(false);
    setBulkProgress({ current: 0, total: 0 });
    setSelectedRowKeys([]);
    queryClient.invalidateQueries({ queryKey: ['sku-migration-status'] });
  };

  const handleGenerateAllNeedingSkus = () => {
    if (!statuses || productsNeedingGeneration.length === 0) return;

    Modal.confirm({
      title: 'Generate SKUs for all products that need migration?',
      content: (
        <div>
          <p>
            {`Runs legacy → SKU generation for ${productsNeedingGeneration.length} product(s) (status Ready or Partial). Requests are spaced one second apart to reduce load.`}
          </p>
        </div>
      ),
      okText: 'Start',
      width: 520,
      onOk: () => handleBulkGenerateList(productsNeedingGeneration),
    });
  };

  const handleBulkGenerateSelected = () => {
    if (!statuses || generatableSelectedKeys.length === 0) {
      return undefined;
    }

    const productsToGenerate = generatableSelectedKeys.map(
      (key) => statuses.find((s) => s.productId === key),
    ).filter(Boolean);

    handleBulkGenerateList(productsToGenerate);
    return undefined;
  };

  // Compute summary statistics
  const summary = useMemo(() => {
    if (!statuses) {
      return {
        ready: 0, partial: 0, complete: 0, noData: 0,
      };
    }
    return {
      ready: statuses.filter((s) => s.status === 'ready').length,
      partial: statuses.filter((s) => s.status === 'partial').length,
      complete: statuses.filter((s) => s.status === 'complete').length,
      noData: statuses.filter((s) => s.status === 'no_legacy_data').length,
    };
  }, [statuses]);

  // Filter data
  const filteredData = useMemo(() => {
    if (!statuses) return [];
    return statuses.filter((item) => {
      const matchesSearch = !searchText
        || (item.productName && item.productName.toLowerCase().includes(searchText.toLowerCase()));
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [statuses, searchText, statusFilter]);

  const handleSelectAllVisible = () => {
    const allVisibleKeys = filteredData.map((item) => item.productId);
    const allSelected = allVisibleKeys.length > 0
      && allVisibleKeys.every((key) => selectedRowKeys.includes(key));

    if (allSelected) {
      // Deselect all visible rows
      setSelectedRowKeys(
        selectedRowKeys.filter((key) => !allVisibleKeys.includes(key)),
      );
    } else {
      // Select all visible rows (merge with existing selection)
      const merged = new Set([...selectedRowKeys, ...allVisibleKeys]);
      setSelectedRowKeys([...merged]);
    }
  };

  const columns = [
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      sorter: (a, b) => (a.productName || '').localeCompare(b.productName || ''),
      render: (name, record) => (
        <a href={`/dashboard/product-database/products/${record.productId}`}>
          {name || `Product ${record.productId}`}
        </a>
      ),
    },
    {
      title: 'Detectable SKUs',
      dataIndex: 'candidateCount',
      key: 'candidateCount',
      sorter: (a, b) => a.candidateCount - b.candidateCount,
      align: 'center',
      width: 140,
    },
    {
      title: 'Existing SKUs',
      dataIndex: 'existingCount',
      key: 'existingCount',
      sorter: (a, b) => a.existingCount - b.existingCount,
      align: 'center',
      width: 130,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: (a, b) => {
        const order = {
          ready: 0, partial: 1, complete: 2, no_legacy_data: 3,
        };
        return (order[a.status] || 99) - (order[b.status] || 99);
      },
      render: (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.no_legacy_data;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: 'SKU barcodes',
      dataIndex: 'skuBarcodeCoverage',
      key: 'skuBarcodeCoverage',
      width: 110,
      align: 'center',
      render: (v) => {
        const c = COVERAGE_CONFIG[v] || COVERAGE_CONFIG.none;
        return <Tag color={c.color}>{c.label}</Tag>;
      },
    },
    {
      title: 'SKU pricing',
      dataIndex: 'skuPricingCoverage',
      key: 'skuPricingCoverage',
      width: 110,
      align: 'center',
      render: (v) => {
        const c = COVERAGE_CONFIG[v] || COVERAGE_CONFIG.none;
        return <Tag color={c.color}>{c.label}</Tag>;
      },
    },
    {
      title: 'SKU Foodstuffs',
      dataIndex: 'skuFoodstuffsCoverage',
      key: 'skuFoodstuffsCoverage',
      width: 120,
      align: 'center',
      render: (v) => {
        const c = COVERAGE_CONFIG[v] || COVERAGE_CONFIG.none;
        return <Tag color={c.color}>{c.label}</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      align: 'center',
      render: (_, record) => {
        if (record.status === 'complete' || record.status === 'no_legacy_data') {
          return <Text type="secondary">—</Text>;
        }
        const isGenerating = generatingProductId === record.productId;
        return (
          <Button
            type="primary"
            size="small"
            icon={isGenerating ? <LoadingOutlined /> : <ThunderboltOutlined />}
            onClick={() => handleGenerate(record.productId)}
            loading={isGenerating}
            disabled={bulkGenerating}
          >
            Generate
          </Button>
        );
      },
    },
  ];

  return (
    <div className="sku-migration-table">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Phase 1 — data completeness"
        description={(
          <span>
            {'Use '}
            <strong>Run barcode validation</strong>
            {' to compare legacy product barcode columns with '}
            <code>product_sku</code>
            {' bn_* fields. Generate SKUs for Ready/Partial rows individually, via selection, or '}
            <strong>Generate all (Ready + Partial)</strong>
            {' when you are ready to bulk-fill.'}
          </span>
        )}
      />

      {/* Summary Cards — click to toggle filter */}
      <div className="sku-migration-summary">
        <Row gutter={16}>
          <Col span={6}>
            <Card
              size="small"
              className={`summary-card${statusFilter === 'ready' ? ' active-ready' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'ready' ? 'all' : 'ready')}
            >
              <Statistic
                title="Ready"
                value={summary.ready}
                valueStyle={{ color: '#faad14' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card
              size="small"
              className={`summary-card${statusFilter === 'partial' ? ' active-partial' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'partial' ? 'all' : 'partial')}
            >
              <Statistic
                title="Partial"
                value={summary.partial}
                valueStyle={{ color: '#fa8c16' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card
              size="small"
              className={`summary-card${statusFilter === 'complete' ? ' active-complete' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'complete' ? 'all' : 'complete')}
            >
              <Statistic
                title="Complete"
                value={summary.complete}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card
              size="small"
              className={`summary-card${statusFilter === 'no_legacy_data' ? ' active-no-data' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'no_legacy_data' ? 'all' : 'no_legacy_data')}
            >
              <Statistic
                title="No Data"
                value={summary.noData}
                prefix={<MinusCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Bulk Progress */}
      {bulkGenerating && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Text>
            {'Generating SKUs... '}
            {bulkProgress.current}
            {' / '}
            {bulkProgress.total}
          </Text>
          <Progress
            percent={Math.round((bulkProgress.current / bulkProgress.total) * 100)}
            status="active"
          />
        </Card>
      )}

      {/* Actions & Filters */}
      <div className="sku-migration-actions">
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isLoading}
          >
            Refresh
          </Button>
          <Tooltip
            title={
              selectedRowKeys.length > 0
                ? `Validate the ${selectedRowKeys.length} selected product${selectedRowKeys.length === 1 ? '' : 's'} only`
                : 'Validate all products. Select rows first to scope to specific products.'
            }
          >
            <Button
              icon={<AuditOutlined />}
              onClick={runValidationReport}
              loading={validationLoading}
            >
              {selectedRowKeys.length > 0
                ? `Run barcode validation (${selectedRowKeys.length} selected)`
                : 'Run barcode validation (all)'}
            </Button>
          </Tooltip>
          <Button
            type="primary"
            ghost
            icon={<ThunderboltOutlined />}
            onClick={handleGenerateAllNeedingSkus}
            disabled={bulkGenerating || productsNeedingGeneration.length === 0}
          >
            {`Generate all (Ready + Partial) (${productsNeedingGeneration.length})`}
          </Button>
          <Button
            onClick={handleSelectAllVisible}
            disabled={bulkGenerating || filteredData.length === 0}
          >
            <Checkbox
              checked={
                filteredData.length > 0
                && filteredData.every((item) => selectedRowKeys.includes(item.productId))
              }
              indeterminate={
                filteredData.some((item) => selectedRowKeys.includes(item.productId))
                && !filteredData.every((item) => selectedRowKeys.includes(item.productId))
              }
              style={{ marginRight: 6 }}
            />
            {`Select All (${filteredData.length})`}
          </Button>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={handleBulkGenerateSelected}
            disabled={generatableSelectedKeys.length === 0 || bulkGenerating}
          >
            {`Generate Selected (${generatableSelectedKeys.length})`}
          </Button>
        </Space>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Input
            placeholder="Search products..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
        </div>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="productId"
        loading={isLoading}
        size="small"
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          columnWidth: 48,
        }}
        pagination={{
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100', '200'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} products`,
        }}
      />

      <Modal
        title="Barcode validation (legacy vs SKU)"
        visible={validationOpen}
        onCancel={() => setValidationOpen(false)}
        footer={null}
        width={1500}
        destroyOnClose
        bodyStyle={{ paddingTop: 16 }}
      >
        {validationReport?.summary && (() => {
          const issues = validationReport.issues || [];
          const validationSummary = validationReport.summary;
          const masterLoadedLabel = validationSummary.masterLoadedAt
            ? dayjs(validationSummary.masterLoadedAt).fromNow()
            : null;
          const counts = ISSUE_FILTER_ORDER.reduce((acc, key) => {
            acc[key] = 0;
            return acc;
          }, {});
          issues.forEach((i) => {
            if (counts[i.issue] != null) counts[i.issue] += 1;
          });
          const filteredIssues = validationIssueFilter === 'all'
            ? issues
            : issues.filter((i) => i.issue === validationIssueFilter);

          return (
            <>
              <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                <Space wrap size="large">
                  {validationScopeLabel && (
                    <Text type="secondary">
                      {'Scope: '}
                      <Text strong>{validationScopeLabel}</Text>
                    </Text>
                  )}
                  <Text>
                    <strong>{validationReport.summary.productsChecked}</strong>
                    {' checked'}
                  </Text>
                  <Text>
                    <strong>{validationReport.summary.productsWithIssues}</strong>
                    {' with issues'}
                  </Text>
                  <Text>
                    <strong>{validationReport.summary.totalIssues}</strong>
                    {' total issue row(s)'}
                  </Text>
                  {validationSummary.masterBarcodeCount != null && (
                    <Text type="secondary">
                      {'Master sheet: '}
                      <Text strong>{validationSummary.masterBarcodeCount.toLocaleString()}</Text>
                      {' barcodes loaded'}
                      {masterLoadedLabel ? ` · refreshed ${masterLoadedLabel}` : ''}
                    </Text>
                  )}
                  {validationReport.summary.truncated && (
                    <Text type="warning">
                      {`Showing first ${validationReport.summary.limitReturned} of ${validationReport.summary.totalIssues}. Scope the request to fewer products to see them all.`}
                    </Text>
                  )}
                </Space>
              </Card>

              <div className="validation-filter-bar">
                <Radio.Group
                  value={validationIssueFilter}
                  onChange={(e) => setValidationIssueFilter(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="all">
                    {`All (${issues.length})`}
                  </Radio.Button>
                  {ISSUE_FILTER_ORDER.map((key) => {
                    const cfg = ISSUE_CONFIG[key];
                    return (
                      <Radio.Button
                        key={key}
                        value={key}
                        disabled={counts[key] === 0}
                      >
                        <Space size={4}>
                          {cfg.icon}
                          {`${cfg.label} (${counts[key]})`}
                        </Space>
                      </Radio.Button>
                    );
                  })}
                </Radio.Group>
              </div>

              <Table
                className="validation-issue-table"
                size="small"
                rowKey={(row, index) => `${row.productId}-${row.legacyField}-${row.issue}-${index}`}
                columns={buildValidationIssueColumns({
                  onApplyFix: handleApplyFix,
                  applyingFixKey,
                })}
                dataSource={filteredIssues}
                rowClassName={(row) => ISSUE_CONFIG[row.issue]?.rowClass || ''}
                pagination={{
                  pageSize: 25,
                  showSizeChanger: true,
                  pageSizeOptions: ['25', '50', '100'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} issues`,
                }}
                scroll={{ x: 2040 }}
                locale={{
                  emptyText: validationIssueFilter === 'all'
                    ? 'No issues found.'
                    : `No "${ISSUE_CONFIG[validationIssueFilter]?.label}" issues in this scope.`,
                }}
              />
            </>
          );
        })()}
      </Modal>

      <Modal
        title="Apply barcode fix"
        visible={Boolean(fixConfirmRow)}
        onCancel={() => {
          if (!applyingFixKey) setFixConfirmRow(null);
        }}
        onOk={handleConfirmApplyFix}
        okText="Apply fix"
        okButtonProps={{
          loading: Boolean(applyingFixKey),
          icon: <ToolOutlined />,
        }}
        cancelButtonProps={{ disabled: Boolean(applyingFixKey) }}
        width={640}
        destroyOnClose
      >
        {fixConfirmRow && (
          <>
            <Text>
              {'Product: '}
              <Text strong>{fixConfirmRow.productName}</Text>
            </Text>
            <div style={{ marginTop: 12, marginBottom: 12 }}>
              <Text type="secondary">This will update SKU rows only. Legacy product barcode columns are not modified.</Text>
            </div>
            {fixConfirmRow.remediation?.warnings?.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Warnings"
                description={(
                  <ul className="remediation-warning-list">
                    {fixConfirmRow.remediation.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                )}
              />
            )}
            <Card size="small" title="Planned steps" bodyStyle={{ padding: 12 }}>
              <ol className="remediation-step-list">
                {(fixConfirmRow.remediation?.steps || []).map((step) => (
                  <li key={step.label || step.type}>
                    <Text>{step.label}</Text>
                  </li>
                ))}
              </ol>
            </Card>
          </>
        )}
      </Modal>
    </div>
  );
};

export default SkuMigrationTable;
